<template>
  <div class="storeCouponsHistory">
    <!-- 商家发券记录页：根据 gardenId 和 userId 展示该商家的发券记录，并提供修改、删除入口。 -->
    <el-card>
      <div class="jump-btn">
        <el-button type="primary" @click="jump">{{ $t('lang.btn_backpage') }}</el-button>
      </div>

      <!-- 记录列表通过接口一次性取回后在前端分页；修改、删除按钮都受 startTime 时间门槛控制。 -->
      <el-table
        :data="tableData"
        class="wid100"
        :height="
          tableData.length > 0
            ? require('@/config/enum').tableheight529
            : require('@/config/enum').tableheight109
        "
        :row-class-name="tableRowClassName"
      >
        <el-table-column
          type="index"
          :index="indexMethod"
          align="center"
          :label="$t('lang.lab_index')"
          width="100px"
        ></el-table-column>
        <el-table-column prop="couponName" label="券名称"></el-table-column>
        <el-table-column prop="couponType" label="券类型">
          <template slot-scope="scope">
            <span v-if="Number(scope.row.couponType) === 1">时长减免</span>
            <span v-else-if="Number(scope.row.couponType) === 2">次数减免</span>
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column prop="reduceMinutes" label="减免分钟数">
          <template slot-scope="scope">
            <span>{{ scope.row.reduceMinutes || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="grantCount" label="发券总数"></el-table-column>
        <el-table-column prop="startTime" label="生效开始时间" width="180">
          <template slot-scope="scope">
            <span>{{ scope.row.startTime | time }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" label="生效结束时间" width="180">
          <template slot-scope="scope">
            <span>{{ scope.row.endTime | time }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注">
          <template slot-scope="scope">
            <span>{{ scope.row.remark | valueIsNull }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="operation"
          :label="$t('lang.lab_operation')"
          width="280"
          fixed="right"
        >
          <template slot-scope="scope">
            <el-button @click="useRecord(scope.row)">使用记录</el-button>
            <el-button :disabled="!canEdit(scope.row)" @click="openEditDialog(scope.row)">修改</el-button>
            <el-button class="orange" :disabled="!canEdit(scope.row)" @click="deleteCoupon(scope.row)">{{
              $t('lang.btn_delete')
            }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageNo"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pageSize"
        layout="sizes, prev, pager, next, jumper"
        :page-count="pageCount"
      />
    </el-card>

    <!-- 修改弹窗与发券页表单保持一致，复用同一套车场下拉、联动规则和表单校验。 -->
    <el-dialog
      title="修改发券"
      :visible.sync="editDialogVisible"
      :close-on-click-modal="false"
      center
      top="130px"
      width="600px"
      @closed="handleEditDialogClosed"
    >
      <el-form
        ref="couponForm"
        :model="couponForm"
        :rules="couponRules"
        :label-width="require('@/config/enum').c_ws4"
        :label-suffix="require('@/config/enum').c_colon"
      >
        <el-form-item label="券名称" prop="couponName">
          <el-input
            v-model.trim="couponForm.couponName"
            maxlength="50"
            placeholder="请输入券名称"
          ></el-input>
        </el-form-item>
        <el-form-item label="车场" prop="parkId">
          <el-select
            v-model="couponForm.parkId"
            class="select wid100"
            placeholder="请选择车场"
            :disabled="parkList.length === 0"
          >
            <el-option
              v-for="item in parkList"
              :key="item.id"
              :label="item.parkName"
              :value="item.id"
            ></el-option>
          </el-select>
          <div v-if="parkList.length === 0" class="form-tip">当前小区未配置车场</div>
        </el-form-item>
        <el-form-item label="券类型" prop="couponType">
          <el-select
            v-model="couponForm.couponType"
            class="select wid100"
            placeholder="请选择券类型"
            @change="handleCouponTypeChange"
          >
            <el-option label="时长减免" :value="1"></el-option>
            <el-option label="次数减免" :value="2"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="减免分钟数" prop="reduceMinutes">
          <el-input-number
            v-model="couponForm.reduceMinutes"
            class="wid100"
            :min="couponForm.couponType === 1 ? 30 : 0"
            :step="30"
            :precision="0"
            :controls="true"
            :disabled="couponForm.couponType === 2"
          ></el-input-number>
        </el-form-item>
        <el-form-item label="发券总数" prop="grantCount">
          <el-input-number
            v-model="couponForm.grantCount"
            class="wid100"
            :min="1"
            :step="1"
            :precision="0"
            :controls="true"
          ></el-input-number>
        </el-form-item>
        <el-form-item label="生效开始时间" prop="startTime">
          <el-date-picker
            v-model="couponForm.startTime"
            class="wid100"
            type="datetime"
            value-format="timestamp"
            placeholder="请选择生效开始时间"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="生效结束时间" prop="endTime">
          <el-date-picker
            v-model="couponForm.endTime"
            class="wid100"
            type="datetime"
            value-format="timestamp"
            placeholder="请选择生效结束时间"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input
            v-model.trim="couponForm.remark"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入备注"
          ></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">{{ $t('lang.btn_cancel') }}</el-button>
        <el-button type="primary" :disabled="!couponFormChanged" @click="saveCoupon">{{
          $t('lang.btn_save')
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 商家发券记录页：负责记录查询、本地分页、修改前校验、删除确认以及修改表单提交。
export default {
  name: '/manage/main.storeCouponsHistory',
  data() {
    // 时长券要求减免分钟数必填、为整数且最小 30；次数券统一按 0 处理。
    const validateReduceMinutes = (rule, value, callback) => {
      if (this.couponForm.couponType === 2) {
        callback()
        return
      }
      if (value === '' || value === null || value === undefined) {
        callback(new Error('请输入减免分钟数'))
        return
      }
      if (!Number.isInteger(value)) {
        callback(new Error('减免分钟数必须为整数'))
        return
      }
      if (value < 30) {
        callback(new Error('减免分钟数最小为30'))
        return
      }
      callback()
    }
    // 发券总数必须为正整数。
    const validateGrantCount = (rule, value, callback) => {
      if (value === '' || value === null || value === undefined) {
        callback(new Error('请输入发券总数'))
        return
      }
      if (!Number.isInteger(value) || value <= 0) {
        callback(new Error('发券总数必须为正整数'))
        return
      }
      callback()
    }
    // 修改时结束时间必须晚于开始时间，时间值保持时间戳格式。
    const validateEndTime = (rule, value, callback) => {
      if (value === '' || value === null || value === undefined) {
        callback(new Error('请选择生效结束时间'))
        return
      }
      if (
        this.couponForm.startTime !== '' &&
        this.couponForm.startTime !== null &&
        this.couponForm.startTime !== undefined &&
        value <= this.couponForm.startTime
      ) {
        callback(new Error('生效结束时间必须大于开始时间'))
        return
      }
      callback()
    }

    return {
      // 列表分页与原始记录缓存；接口返回全量记录后在本地切页。
      pageNo: 1,
      pageSize: 10,
      pageCount: 0,
      tableDataAll: [],
      tableData: [],
      // 当前页依赖的路由参数与车场缓存。
      gardenId: '',
      userId: '',
      parkList: [],
      // 修改弹窗显隐状态。
      editDialogVisible: false,
      // 打开修改弹窗时保存一份原始快照，用于判断“未修改不可保存”。
      originalCouponForm: null,
      // 修改表单模型与校验规则，字段与发券页保持一致。
      couponForm: {
        id: '',
        couponName: '',
        couponType: 1,
        reduceMinutes: 30,
        grantCount: 1,
        startTime: '',
        endTime: '',
        remark: '',
        parkId: '',
      },
      couponRules: {
        couponName: [{ required: true, message: '请输入券名称', trigger: 'blur' }],
        parkId: [{ required: true, message: '请选择车场', trigger: 'change' }],
        couponType: [{ required: true, message: '请选择券类型', trigger: 'change' }],
        reduceMinutes: [{ required: true, validator: validateReduceMinutes, trigger: 'change' }],
        grantCount: [{ required: true, validator: validateGrantCount, trigger: 'change' }],
        startTime: [{ required: true, message: '请选择生效开始时间', trigger: 'change' }],
        endTime: [{ required: true, validator: validateEndTime, trigger: 'change' }],
      },
    }
  },
  created() {
    this.initPage()
  },
  activated() {
    this.initPage()
  },
  watch: {
    // 开始时间变化后，联动触发结束时间重新校验。
    'couponForm.startTime'() {
      if (this.$refs.couponForm) {
        this.$refs.couponForm.validateField('endTime')
      }
    },
  },
  computed: {
    // 只有表单值相对原始记录发生变化时，保存按钮才允许点击。
    couponFormChanged() {
      if (!this.originalCouponForm) {
        return false
      }
      return (
        JSON.stringify(this.buildCouponFormSnapshot(this.couponForm)) !==
        JSON.stringify(this.originalCouponForm)
      )
    },
  },
  methods: {
    // 页面进入时从路由读取 gardenId、userId，并分别加载车场缓存和发券记录。
    initPage() {
      this.gardenId = this.$route.query.gardenId || ''
      this.userId = this.$route.query.userId || ''
      if (!this.gardenId || !this.userId) {
        this.tableDataAll = []
        this.tableData = []
        this.pageCount = 0
        this.parkList = []
        this.$message.warning('缺少查询参数')
        return
      }
      this.pageNo = 1
      this.getParkList()
      this.getTableList()
    },
    indexMethod(index) {
      return (this.pageNo - 1) * this.pageSize + index + 1
    },
    tableRowClassName({ rowIndex }) {
      return rowIndex % 2 === 0 ? 'row-dark' : ''
    },
    // 仅允许在记录尚未生效前修改或删除，生效后按钮置灰。
    canEdit(row) {
      return !!row.startTime && Date.now() < Number(row.startTime)
    },
    // 记录页初始化时加载当前小区的车场列表，后续修改弹窗直接复用，不重复请求。
    getParkList() {
      let loading = this.$loading()
      this.$get(`/carPark?pageNo=1&pageSize=999&gardenId=${this.gardenId}`).then(
        res => {
          loading.close()
          if (res && res.errCode === 0) {
            this.parkList = res.list || []
          }
        },
        error => {
          loading.close()
          this.parkList = []
          this.$message.error(error)
        }
      )
    },
    // 查询当前商家的发券记录；兼容不同返回结构后统一写入本地分页缓存。
    getTableList() {
      let loading = this.$loading()
      this.$get(`/carCouponMerchant?gardenId=${this.gardenId}&userId=${this.userId}`).then(
        res => {
          loading.close()
          if (res && res.errCode === 0) {
            let list = []
            // 兼容接口可能返回 list、data 或直接数组的情况。
            if (Array.isArray(res.list)) {
              list = res.list
            } else if (Array.isArray(res.data)) {
              list = res.data
            } else if (Array.isArray(res)) {
              list = res
            }
            this.tableDataAll = list
            this.refreshTableData()
          }
        },
        error => {
          loading.close()
          this.$message.error(error)
        }
      )
    },
    // 根据 pageNo 和 pageSize 从全量记录中截取当前页数据。
    refreshTableData() {
      this.pageCount =
        this.tableDataAll.length === 0 ? 0 : Math.ceil(this.tableDataAll.length / this.pageSize)
      let start = (this.pageNo - 1) * this.pageSize
      let end = start + this.pageSize
      this.tableData = this.tableDataAll.slice(start, end)
    },
    // 打开修改弹窗时回填整条记录，并记录一份标准化快照供“是否改动”判断使用。
    openEditDialog(row) {
      if (!this.canEdit(row)) {
        return
      }
      this.editDialogVisible = true
      this.couponForm = {
        id: row.id,
        couponName: row.couponName || '',
        couponType: Number(row.couponType) || 1,
        reduceMinutes: Number(row.reduceMinutes || 0),
        grantCount: Number(row.grantCount || 1),
        startTime: row.startTime || '',
        endTime: row.endTime || '',
        remark: row.remark || '',
        parkId: row.parkId || '',
      }
      if (this.couponForm.couponType === 1 && this.couponForm.reduceMinutes < 30) {
        this.couponForm.reduceMinutes = 30
      }
      if (this.couponForm.couponType === 2) {
        this.couponForm.reduceMinutes = 0
      }
      this.originalCouponForm = this.buildCouponFormSnapshot(this.couponForm)
      this.$nextTick(() => {
        if (this.$refs.couponForm) {
          this.$refs.couponForm.clearValidate()
        }
      })
    },
    // 将表单值转换为统一结构，避免类型差异影响“是否修改”的判断结果。
    buildCouponFormSnapshot(form) {
      return {
        id: form.id || '',
        couponName: form.couponName || '',
        couponType: Number(form.couponType) || 1,
        reduceMinutes:
          Number(form.couponType) === 2 ? 0 : Number(form.reduceMinutes === '' ? 0 : form.reduceMinutes),
        grantCount: Number(form.grantCount || 0),
        startTime: form.startTime || '',
        endTime: form.endTime || '',
        remark: form.remark || '',
        parkId: form.parkId || '',
      }
    },
    // 切换券类型时同步处理减免分钟数：次数券固定为 0，时长券至少为 30。
    handleCouponTypeChange(val) {
      if (val === 2) {
        this.couponForm.reduceMinutes = 0
      } else if (this.couponForm.reduceMinutes < 30 || this.couponForm.reduceMinutes === 0) {
        this.couponForm.reduceMinutes = 30
      }
      this.$nextTick(() => {
        if (this.$refs.couponForm) {
          this.$refs.couponForm.validateField('reduceMinutes')
        }
      })
    },
    // 关闭或重新打开修改弹窗前，清空表单和原始快照，避免残留上次编辑数据。
    resetCouponForm() {
      this.originalCouponForm = null
      this.couponForm = {
        id: '',
        couponName: '',
        couponType: 1,
        reduceMinutes: 30,
        grantCount: 1,
        startTime: '',
        endTime: '',
        remark: '',
        parkId: '',
      }
      this.$nextTick(() => {
        if (this.$refs.couponForm) {
          this.$refs.couponForm.clearValidate()
        }
      })
    },
    // 弹窗关闭后统一走表单重置。
    handleEditDialogClosed() {
      this.resetCouponForm()
    },
    // 提交修改表单，调用 PATCH /carCouponMerchant/{id}，并在 body 中显式携带 id。
    saveCoupon() {
      if (this.parkList.length === 0) {
        this.$message.warning('当前小区未配置车场')
        return
      }
      this.$refs.couponForm.validate(valid => {
        if (!valid) {
          return false
        }
        let params = {
          id: this.couponForm.id,
          couponName: this.couponForm.couponName,
          couponType: this.couponForm.couponType,
          // 次数券提交时固定写 0，保证与发券页逻辑保持一致。
          reduceMinutes: this.couponForm.couponType === 2 ? 0 : this.couponForm.reduceMinutes,
          grantCount: this.couponForm.grantCount,
          startTime: this.couponForm.startTime,
          endTime: this.couponForm.endTime,
          remark: this.couponForm.remark,
          gardenId: this.gardenId,
          parkId: this.couponForm.parkId,
          userId: this.userId,
        }
        let loading = this.$loading()
        this.$patch(`/carCouponMerchant/${this.couponForm.id}`, params).then(
          res => {
            loading.close()
            if (res && res.errCode === 0) {
              this.$message.success(this.$i18n.t('lang.lab_save_success'))
              this.editDialogVisible = false
              this.getTableList()
            }
          },
          error => {
            loading.close()
            this.$message.error(error)
          }
        )
      })
    },
    // 删除前复用与修改一致的时间判断，只有未生效记录才允许删除。
    deleteCoupon(row) {
      if (!this.canEdit(row)) {
        return
      }
      let loading = this.$loading()
      this.$confirm(this.$i18n.t('lang.lab_isdelete'), this.$i18n.t('lang.lab_prompt'), {
        confirmButtonText: this.$i18n.t('lang.btn_confirm'),
        cancelButtonText: this.$i18n.t('lang.btn_cancel'),
        type: 'warning',
      })
        .then(() => {
          this.$delete(`/carCouponMerchant/${row.id}`).then(
            res => {
              loading.close()
              if (res && res.errCode === 0) {
                this.$message({
                  type: 'success',
                  message: this.$i18n.t('lang.lab_del_success'),
                })
                this.getTableList()
              }
            },
            error => {
              loading.close()
              this.$message.error(error)
            }
          )
        })
        .catch(() => {
          loading.close()
          this.$message({
            type: 'info',
            message: this.$i18n.t('lang.lab_del_can'),
          })
        })
    },
    handleCurrentChange(val) {
      this.pageNo = val
      this.refreshTableData()
    },
    handleSizeChange(val) {
      this.pageNo = 1
      this.pageSize = val
      this.refreshTableData()
    },
    // 使用记录入口先保留占位，后续再接业务逻辑。
    useRecord(row) {
      this.$router.push({
        name: 'storeCouponsRecord',
        query: {
          couponMerchantId: row.id || '',
        },
      })
    },
    // 返回上一页商家发券列表。
    jump() {
      this.$router.go(-1)
    },
  },
}
</script>

<style lang="scss">
/* 记录页仅补充基础布局样式，表单提示色与发券页保持一致。 */
.storeCouponsHistory {
  .wid100 {
    width: 100%;
  }

  .el-button.orange.is-disabled,
  .el-button.orange.is-disabled:hover,
  .el-button.orange.is-disabled:focus {
    color: #c0c4cc !important;
    opacity: 1;
  }

  .jump-btn {
    margin-bottom: 15px;
  }

  .form-tip {
    color: #f56c6c;
    line-height: 20px;
    font-size: 12px;
  }
}
</style>
